import { useEffect } from 'react';

import {
    useGetCatalogProductsQuery,
    UpdatedProductsSubscription,
    CatalogProductsFilterInput,
    ProductState,
} from 'data/__generated__';
import { UPDATED_PRODUCTS_SUBSCRIPTION } from 'data/subscriptions/product';

import { DataTable } from 'components/DataTable';

import { productsColumns, type ProductRow } from '../constants/productsColumns';

const defaultFilters: CatalogProductsFilterInput = {
    states: [ProductState.Regular, ProductState.Created],
};

export const ProductsList = () => {
    const {
        loading: productsLoading,
        data: productsData,
        error: productsError,
        fetchMore,
        subscribeToMore,
        refetch,
    } = useGetCatalogProductsQuery({
        variables: {
            offset: 0,
            filters: defaultFilters,
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        const unsubscribe = subscribeToMore<UpdatedProductsSubscription>({
            document: UPDATED_PRODUCTS_SUBSCRIPTION,
            updateQuery: (prevData, { subscriptionData }) => {
                if (!subscriptionData || !prevData.catalogProducts) return prevData;
                const updatedProducts = subscriptionData.data.updatedProducts;
                const updatedProductIds = updatedProducts.map((product) => product._id);
                return {
                    catalogProducts: {
                        ...prevData.catalogProducts,
                        products: [
                            ...updatedProducts,
                            ...prevData.catalogProducts.products.filter(
                                (product) => !updatedProductIds.includes(product._id),
                            ),
                        ],
                    },
                };
            },
        });

        return () => unsubscribe();
    }, [subscribeToMore]);

    if (productsError) {
        throw new Error('Une erreur est survenue lors de la récupération des produits');
    }

    const hasMore = productsData?.catalogProducts.hasMore ?? false;
    const products = productsData?.catalogProducts.products ?? [];

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            <DataTable<ProductRow, CatalogProductsFilterInput>
                columns={productsColumns}
                data={products}
                filterMode="server"
                getRowId={(row) => row._id}
                getRowLink={(row) => `/product/${row._id}`}
                hasMore={hasMore}
                isLoading={productsLoading}
                refetch={async (vars) => {
                    await refetch({ ...vars });
                }}
                fetchMore={async (opts) => {
                    await fetchMore({ variables: opts.variables });
                }}
                defaultFilters={defaultFilters}
                searchPlaceholder="Nom, marque ou id du produit"
                searchFilterKey="searchValue"
            />
        </div>
    );
};
